import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Bar,
} from "recharts";

const PALETTE = {
  primary: "#39678f",
  passed: "#6cc850",
  failed: "#b30035",
  pending: "#ccb795",
  slate: "#33424f",
  grid: "#33424f22",
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-[#33424f]/30 p-3 shadow-md">
      <div className="text-[10px] subtitle uppercase tracking-[0.2em] text-[#50493b] mb-1">
        {label}
      </div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-xs font-body">
          <span className="w-2 h-2" style={{ background: p.color }} />
          <span className="text-[#50493b]">{p.name}:</span>
          <span className="subtitle text-[#33424f]">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);

  useEffect(() => {
    // Read configured window from settings; fall back to 6 if endpoint fails.
    api.get("/settings").then(
      (r) => setMonths(Number(r.data?.analytics_months) || 6),
      () => setMonths(6),
    );
  }, []);

  useEffect(() => {
    if (!months) return;
    api.get("/analytics", { params: { months } }).then((r) => setData(r.data));
  }, [months]);

  const maxCellValue = useMemo(() => {
    if (!data) return 1;
    return Math.max(
      1,
      ...data.heatmap.rows.flatMap((r) =>
        data.heatmap.codes.map((c) => r[c] || 0)
      )
    );
  }, [data]);

  const cellColor = useCallback(
    (v) => {
      if (!v) return "bg-[#f5f1ec] text-[#ccb795]";
      const intensity = v / maxCellValue;
      if (intensity < 0.25) return "bg-[#fdd6df] text-[#7a0024]";
      if (intensity < 0.55) return "bg-[#f7a3b5] text-[#7a0024]";
      if (intensity < 0.8) return "bg-[#d83659] text-white";
      return "bg-[#7a0024] text-white";
    },
    [maxCellValue],
  );

  if (!data) {
    return (
      <div className="border border-[#33424f]/20 bg-white p-10 text-sm text-[#50493b] font-body">
        Loading analytics…
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 border border-[#33424f]/20 bg-white">
      {/* Trend chart */}
      <div className="lg:col-span-3 p-6 border-b lg:border-b-0 lg:border-r border-[#33424f]/20" data-testid="analytics-trend">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="subtitle text-[10px] uppercase tracking-[0.25em] text-[#50493b]">
              Last {months} {months === 1 ? "month" : "months"}
            </div>
            <h3 className="font-display text-2xl tracking-tight text-[#33424f] leading-none mt-1">
              PASS-RATE TREND
            </h3>
          </div>
        </div>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <ComposedChart data={data.trend} margin={{ top: 10, right: 14, bottom: 0, left: -10 }}>
              <CartesianGrid stroke={PALETTE.grid} strokeDasharray="0" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#50493b", fontFamily: "Montserrat" }}
                axisLine={{ stroke: PALETTE.grid }}
                tickLine={false}
                interval={0}
                padding={{ left: 18, right: 18 }}
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11, fill: "#50493b", fontFamily: "Montserrat" }}
                axisLine={{ stroke: PALETTE.grid }}
                tickLine={false}
                domain={[0, "dataMax + 2"]}
                allowDecimals={false}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 11, fill: "#39678f", fontFamily: "Montserrat" }}
                axisLine={{ stroke: PALETTE.grid }}
                tickLine={false}
                domain={[0, 100]}
                unit="%"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: 10, fontFamily: "Montserrat", textTransform: "uppercase", letterSpacing: "0.15em", color: "#50493b" }}
                iconSize={8}
              />
              <Bar yAxisId="left" dataKey="passed" stackId="a" fill={PALETTE.passed} name="Passed" maxBarSize={36} />
              <Bar yAxisId="left" dataKey="failed" stackId="a" fill={PALETTE.failed} name="Failed" maxBarSize={36} />
              <Bar yAxisId="left" dataKey="pending" stackId="a" fill={PALETTE.pending} name="Pending" maxBarSize={36} />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="pass_rate"
                stroke={PALETTE.primary}
                strokeWidth={2.5}
                dot={{ r: 4, fill: PALETTE.primary, strokeWidth: 0 }}
                name="Pass rate %"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Heatmap */}
      <div className="lg:col-span-2 p-6" data-testid="analytics-heatmap">
        <div className="mb-4">
          <div className="subtitle text-[10px] uppercase tracking-[0.25em] text-[#50493b]">
            Leak points · last {months} {months === 1 ? "month" : "months"}
          </div>
          <h3 className="font-display text-2xl tracking-tight text-[#33424f] leading-none mt-1">
            PTF HEATMAP
          </h3>
          <div className="text-xs font-body text-[#50493b] mt-1">
            {data.heatmap.total_failures} failure{data.heatmap.total_failures !== 1 ? "s" : ""} in window
          </div>
        </div>
        <div className="w-full">
          <table className="w-full text-xs tabular-nums table-fixed">
            <colgroup>
              <col className="w-[4.5rem]" />
              {data.heatmap.codes.map((c) => (
                <col key={c} />
              ))}
              <col className="w-6" />
            </colgroup>
            <thead>
              <tr>
                <th className="text-left subtitle uppercase tracking-[0.15em] text-[10px] text-[#50493b] pb-2 pr-1 overflow-hidden text-ellipsis whitespace-nowrap">
                  Vessel
                </th>
                {data.heatmap.codes.map((c) => (
                  <th key={c} className="subtitle uppercase tracking-[0.1em] text-[10px] text-[#50493b] pb-2 px-0.5 overflow-hidden">
                    {c}
                  </th>
                ))}
                <th className="subtitle uppercase tracking-[0.15em] text-[10px] text-[#50493b] pb-2 pl-1">
                  Σ
                </th>
              </tr>
            </thead>
            <tbody>
              {data.heatmap.rows.map((r) => (
                <tr key={r.vessel_type}>
                  <td className="subtitle py-1 pr-1 text-[#33424f] whitespace-nowrap overflow-hidden text-ellipsis" title={r.vessel_type}>{r.vessel_type}</td>
                  {data.heatmap.codes.map((c) => (
                    <td key={c} className="px-0.5 py-0.5">
                      <div
                        className={`h-7 flex items-center justify-center subtitle ${cellColor(r[c])}`}
                        title={`${r.vessel_type} × ${c}: ${r[c]}`}
                      >
                        {r[c] || ""}
                      </div>
                    </td>
                  ))}
                  <td className="subtitle py-1 pl-1 text-[#33424f]">{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
